/**
 * TriggerEditDialog (P3-E Doc 40) — Add/edit webhook hoặc cron trigger.
 */
import { Clock, Webhook, X } from "lucide-react";
import * as React from "react";

import {
  type CronPreset,
  type CreateTriggerRequest,
  type TriggerType,
  type WebhookSecretReveal,
  type WorkflowTrigger,
  isWebhookReveal,
  triggersApi,
} from "@/api/workflowTriggers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { useUIStore } from "@/stores/ui";

interface Props {
  open: boolean;
  workflowCode: string;
  initial: WorkflowTrigger | null;
  onClose: () => void;
  onSaved: (revealed: WebhookSecretReveal | null) => void | Promise<void>;
}

function slugify(s: string): string {
  return s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/đ/g, "d")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);
}

export function TriggerEditDialog({
  open,
  workflowCode,
  initial,
  onClose,
  onSaved,
}: Props): JSX.Element | null {
  const isEdit = initial !== null;
  const pushToast = useUIStore((s) => s.pushToast);

  const [type, setType] = React.useState<TriggerType>(
    initial?.trigger_type ?? "webhook",
  );
  const [slug, setSlug] = React.useState(initial?.slug ?? "");
  const [slugTouched, setSlugTouched] = React.useState(false);
  const [name, setName] = React.useState(initial?.name ?? "");
  const [enabled, setEnabled] = React.useState(initial?.enabled ?? true);
  const [cronExpr, setCronExpr] = React.useState(
    String(initial?.config?.cron_expr ?? "0 9 * * *"),
  );
  const [busy, setBusy] = React.useState(false);
  const [presets, setPresets] = React.useState<CronPreset[]>([]);

  React.useEffect(() => {
    if (open) {
      setType(initial?.trigger_type ?? "webhook");
      setSlug(initial?.slug ?? "");
      setSlugTouched(false);
      setName(initial?.name ?? "");
      setEnabled(initial?.enabled ?? true);
      setCronExpr(String(initial?.config?.cron_expr ?? "0 9 * * *"));
    }
  }, [open, initial]);

  React.useEffect(() => {
    if (open && presets.length === 0) {
      void triggersApi.listPresets().then(setPresets).catch(() => {});
    }
  }, [open, presets.length]);

  React.useEffect(() => {
    if (!isEdit && !slugTouched) {
      setSlug(slugify(name));
    }
  }, [name, slugTouched, isEdit]);

  if (!open) return null;

  const validate = (): string | null => {
    if (!name.trim()) return "Vui lòng nhập tên";
    if (!slug.trim()) return "Vui lòng nhập slug";
    if (!/^[a-z0-9][a-z0-9_-]{0,62}$/.test(slug)) {
      return "Slug chỉ a-z 0-9 _ -";
    }
    if (type === "cron" && !cronExpr.trim()) return "Cần cron expression";
    return null;
  };

  const handleSave = async (): Promise<void> => {
    const err = validate();
    if (err) {
      pushToast({ variant: "warning", title: err });
      return;
    }
    setBusy(true);
    try {
      if (isEdit && initial) {
        const config: Record<string, unknown> = {};
        if (type === "cron") config.cron_expr = cronExpr.trim();
        await triggersApi.update(initial.id, {
          name: name.trim(),
          enabled,
          config: type === "cron" ? config : undefined,
        });
        pushToast({ variant: "success", title: "Đã cập nhật" });
        await onSaved(null);
        onClose();
      } else {
        const config: Record<string, unknown> = {};
        if (type === "cron") config.cron_expr = cronExpr.trim();

        const req: CreateTriggerRequest = {
          workflow_code: workflowCode,
          trigger_type: type,
          slug: slug.trim(),
          name: name.trim(),
          enabled,
          config,
        };
        const res = await triggersApi.create(req);
        pushToast({ variant: "success", title: "Đã tạo trigger" });
        if (isWebhookReveal(res)) {
          await onSaved(res);
        } else {
          await onSaved(null);
        }
        onClose();
      }
    } catch (e) {
      pushToast({
        variant: "destructive",
        title: "Lưu thất bại",
        description: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-xl border bg-popover shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between border-b px-4 py-3">
          <h2 className="text-sm font-semibold">
            {isEdit ? "Sửa trigger" : "Thêm trigger"}
          </h2>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground"
          >
            <X className="size-4" />
          </button>
        </header>

        <div className="p-4 space-y-4">
          {!isEdit ? (
            <div>
              <label className="text-xs font-medium block mb-1.5">Loại</label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setType("webhook")}
                  className={cn(
                    "rounded-md border p-2.5 text-left transition-all",
                    type === "webhook"
                      ? "border-primary ring-1 ring-primary/30 bg-gradient-brand-soft"
                      : "hover:border-primary/50",
                  )}
                >
                  <Webhook className="size-4 mb-1 text-violet-600" />
                  <div className="text-xs font-medium">Webhook</div>
                  <div className="text-[10px] text-muted-foreground mt-0.5">
                    External system POST với HMAC signature
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => setType("cron")}
                  className={cn(
                    "rounded-md border p-2.5 text-left transition-all",
                    type === "cron"
                      ? "border-primary ring-1 ring-primary/30 bg-gradient-brand-soft"
                      : "hover:border-primary/50",
                  )}
                >
                  <Clock className="size-4 mb-1 text-blue-600" />
                  <div className="text-xs font-medium">Cron schedule</div>
                  <div className="text-[10px] text-muted-foreground mt-0.5">
                    Tự chạy theo lịch (every 5 min, daily 9am, ...)
                  </div>
                </button>
              </div>
            </div>
          ) : null}

          <div>
            <label className="text-xs font-medium block mb-1">Tên</label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={
                type === "webhook"
                  ? "VD: Jira PR webhook"
                  : "VD: Daily 9am report"
              }
              autoFocus
            />
          </div>

          <div>
            <label className="text-xs font-medium block mb-1">
              Slug
              <span className="ml-1 text-muted-foreground">
                (path identifier)
              </span>
            </label>
            <Input
              value={slug}
              onChange={(e) => {
                setSlug(e.target.value);
                setSlugTouched(true);
              }}
              placeholder="jira-pr-webhook"
              disabled={isEdit}
              className="font-mono text-sm"
            />
          </div>

          {type === "cron" ? (
            <div>
              <label className="text-xs font-medium block mb-1">
                Cron schedule
              </label>
              {presets.length > 0 ? (
                <select
                  value={
                    presets.find((p) => p.expr === cronExpr.trim())?.expr ??
                    "__custom__"
                  }
                  onChange={(e) => {
                    if (e.target.value !== "__custom__") {
                      setCronExpr(e.target.value);
                    }
                  }}
                  className="w-full h-8 rounded-md border bg-background px-2 text-sm mb-2"
                >
                  {presets.map((p) => (
                    <option key={p.key} value={p.expr}>
                      {p.label} ({p.expr})
                    </option>
                  ))}
                  <option value="__custom__">— Custom (raw cron) —</option>
                </select>
              ) : null}
              <Input
                value={cronExpr}
                onChange={(e) => setCronExpr(e.target.value)}
                placeholder="0 9 * * *"
                className="font-mono text-sm"
              />
              <p className="text-[10px] text-muted-foreground mt-1">
                Format: <code>min hour dom month dow</code>. UTC timezone. Ví
                dụ: <code>*/5 * * * *</code> (every 5 min),{" "}
                <code>0 9 * * 1</code> (Monday 9am).
              </p>
            </div>
          ) : null}

          {type === "webhook" && !isEdit ? (
            <div className="text-[11px] text-muted-foreground bg-muted/50 rounded-md p-2.5 space-y-1">
              <p className="font-medium text-foreground">
                Sau khi tạo, bạn sẽ nhận:
              </p>
              <ul className="list-disc list-inside space-y-0.5">
                <li>Webhook URL: <code>/api/v1/triggers/{slug || "your-slug"}</code></li>
                <li>HMAC secret (chỉ hiển thị 1 lần — save ngay)</li>
                <li>Sample curl command để test</li>
              </ul>
            </div>
          ) : null}

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
            />
            Enabled
          </label>
        </div>

        <footer className="flex justify-end gap-2 border-t px-4 py-3">
          <Button variant="outline" onClick={onClose} disabled={busy}>
            Huỷ
          </Button>
          <Button variant="brand" onClick={() => void handleSave()} disabled={busy}>
            {busy ? "Đang lưu..." : isEdit ? "Cập nhật" : "Tạo trigger"}
          </Button>
        </footer>
      </div>
    </div>
  );
}
